import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useDataRefresh } from "@/hooks/useDataRefresh";
import { toast } from "sonner";
import { studentShortLabel } from "@/lib/utils-app";
import {
  Plus,
  Power,
  Pencil,
  GraduationCap,
  Phone,
  Mail,
  Filter,
  Search,
  FileCheck2,
  AlertTriangle,
  Receipt,
  X,
  Copy,
  Trash2,
} from "lucide-react";
import { Download, FileSpreadsheet } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import StudentDialog from "./StudentDialog";
import StudentSummaryDialog from "./StudentSummaryDialog";
import InvoicesDetailDialog from "./InvoicesDetailDialog";
import MissingDocsDialog from "./MissingDocsDialog";
import DeleteStudentDialog from "./DeleteStudentDialog";
import DeactivateStudentDialog from "./DeactivateStudentDialog";
import ExportStudentsDialog from "./ExportStudentsDialog";
import PrintableSheetDialog from "./PrintableSheetDialog";
import { computeDocsStatus, studentInvoices } from "@/lib/student-docs";
import { useAuth } from "@/contexts/AuthContext";

// V1.27 · FASE 1 · Badge visual del ciclo comercial.
const LIFECYCLE_STAGES = {
  prospecto:      { label: "Prospecto",      icon: "🔵", color: "bg-blue-100 text-blue-800 border-blue-200" },
  interesado:     { label: "Interesado",     icon: "🟡", color: "bg-amber-100 text-amber-800 border-amber-200" },
  alumno_activo:  { label: "Alumno activo",  icon: "🟢", color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  alumno_pausa:   { label: "En pausa",       icon: "🟣", color: "bg-purple-100 text-purple-800 border-purple-200" },
  baja:           { label: "Baja",           icon: "⚫", color: "bg-neutral-100 text-neutral-700 border-neutral-200" },
  // V1.41 · Estados de cierre coherentes con el estado administrativo Inactivo.
  apto:              { label: "Apto",              icon: "🏆", color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  no_interesado:     { label: "No interesado",     icon: "🔴", color: "bg-rose-100 text-rose-800 border-rose-200" },
  traslado:          { label: "Traslado",          icon: "🔷", color: "bg-sky-100 text-sky-800 border-sky-200" },
  archivo_historico: { label: "Archivo histórico", icon: "📦", color: "bg-stone-100 text-stone-700 border-stone-200" },
};

function StageBadge({ stage }) {
  const meta = LIFECYCLE_STAGES[stage] || LIFECYCLE_STAGES.prospecto;
  return (
    <span
      data-testid={`stage-badge-${stage || "prospecto"}`}
      title={meta.label}
      className={`inline-flex items-center gap-1 rounded-full border px-1.5 h-5 text-[10px] font-semibold ${meta.color}`}
    >
      <span className="text-[10px]">{meta.icon}</span>
      <span className="hidden sm:inline">{meta.label}</span>
    </span>
  );
}


/** V1.20 · localStorage flag para mostrar/ocultar columna estadísticas. */
const COL_STATS_KEY = "natur.showStatsColumn";

function matchesSearch(student, query) {
  if (!query.trim()) return true;
  const q = query.toLowerCase();
  return (
    student.name.toLowerCase().includes(q) ||
    student.phone.toLowerCase().includes(q) ||
    (student.email || "").toLowerCase().includes(q)
  );
}

export default function StudentsTab({ initialFilter, onFilterConsumed, onNavigate } = {}) {
  const { user } = useAuth();
  const isSuperAdmin = !!user?.is_super_admin;
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  // V1.38 · Por defecto sólo se muestran alumnos activos. El botón "Todos"
  // los muestra a todos incluyendo bajas / inactivos.
  const [activeOnly, setActiveOnly] = useState(true);
  // V1.38 · Filtro por profesor asignado
  const [teacherFilter, setTeacherFilter] = useState("");
  const [teacherOptions, setTeacherOptions] = useState([]); // [{code,label}]
  // V1.39 · Filtro ad-hoc desde Dashboard (bucket bono, docs pendientes, etc.)
  const [idsFilter, setIdsFilter] = useState(null); // Set<string> | null
  const [filterLabel, setFilterLabel] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // V1.38 · Fuente única de profesores = `/teachers` (Profesorado UI).
        const { data } = await api.get("/teachers", { params: { include_inactive: false } });
        if (cancelled) return;
        const opts = (Array.isArray(data) ? data : [])
          .filter((p) => p.active !== false)
          .map((p) => {
            const name = (p.name || "").trim();
            const parts = name.split(/\s+/).filter(Boolean);
            // Código: iniciales del primer y segundo nombre; si sólo hay
            // una palabra (ej. "Gaby") usamos las 2 primeras letras para
            // seguir teniendo un código de 2 caracteres consistente.
            let code = "";
            if (parts.length >= 2) {
              code = (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
            } else if (parts.length === 1) {
              code = parts[0].slice(0, 2).toUpperCase();
            }
            return { code, label: name || code };
          })
          .filter((o) => o.code);
        // dedupe por code preservando primer label
        const seen = new Set();
        setTeacherOptions(opts.filter((o) => (seen.has(o.code) ? false : (seen.add(o.code), true))));
      } catch (_) {
        if (!cancelled) setTeacherOptions([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  const [docsFilter, setDocsFilter] = useState("all"); // all | incomplete | complete | invoice-pending | invoice-emitted
  const [missingDoc, setMissingDoc] = useState("all"); // "all" or specific document name
  const [sheetOpen, setSheetOpen] = useState(false); // V1.71 · hoja imprimible
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [summaryStudent, setSummaryStudent] = useState(null);
  const [invoicesPopoverStudent, setInvoicesPopoverStudent] = useState(null);
  const [missingDocsStudent, setMissingDocsStudent] = useState(null);
  const [deletingStudent, setDeletingStudent] = useState(null);
  const [showExport, setShowExport] = useState(false);
  const [deactivatingStudent, setDeactivatingStudent] = useState(null);
  const [editingMotivoStudent, setEditingMotivoStudent] = useState(null);
  const [duplicating, setDuplicating] = useState(false);
  const [classStats, setClassStats] = useState({});
  const [showStatsColumn, setShowStatsColumn] = useState(() => {
    try { return localStorage.getItem(COL_STATS_KEY) === "1"; } catch { return false; }
  });
  const toggleStatsColumn = useCallback(() => {
    setShowStatsColumn((prev) => {
      const nv = !prev;
      try { localStorage.setItem(COL_STATS_KEY, nv ? "1" : "0"); } catch {}
      return nv;
    });
  }, []);

  // Bulk fetch class stats — only when column is visible (saves bandwidth).
  useEffect(() => {
    if (!showStatsColumn) return;
    api.get("/students/class-stats").then(({ data }) => setClassStats(data || {})).catch(() => {});
  }, [showStatsColumn, students]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/students");
      setStudents(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);
  useDataRefresh(load);

  // V1.39 · Consumimos el filtro venido desde Dashboard: rango de bono
  // (studentIds), profesor, docs pendientes y/o alumno a enfocar.
  // FIX HIGH · No consumimos hasta poder resolver `focusStudentId` (esperar a
  // que `students` esté cargado). Antes se limpiaba el filtro en el primer
  // render (students=[]) y el diálogo nunca llegaba a abrirse.
  useEffect(() => {
    if (!initialFilter) return;
    if (initialFilter.teacher) setTeacherFilter(initialFilter.teacher.toUpperCase());
    if (Array.isArray(initialFilter.studentIds) && initialFilter.studentIds.length > 0) {
      setIdsFilter(new Set(initialFilter.studentIds));
      setFilterLabel(initialFilter.label || "");
      setActiveOnly(false); // los buckets ya vienen filtrados
    }
    if (initialFilter.docsFilter) setDocsFilter(initialFilter.docsFilter);
    if (initialFilter.focusStudentId) {
      if (students.length === 0) return; // esperar a la carga; no consumir aún
      const found = students.find((s) => s.id === initialFilter.focusStudentId);
      if (found) setSummaryStudent(found);
    }
    onFilterConsumed?.();
  }, [initialFilter, students, onFilterConsumed]);

  const studentsWithStatus = useMemo(
    () => students.map((s) => ({ ...s, _docsStatus: computeDocsStatus(s) })),
    [students]
  );

  // Build the union of every applicable doc name across all students,
  // so the "filter by missing document" select reflects custom docs too.
  const docNameOptions = useMemo(() => {
    const set = new Set();
    studentsWithStatus.forEach((s) => {
      s._docsStatus.missing.forEach((n) => set.add(n));
      // Also include delivered docs (so the option is available even when
      // every student has them already delivered)
      (s.documents || []).forEach((d) => {
        if (!d.only_minor) set.add(d.name);
      });
    });
    set.add("Factura emitida");
    return Array.from(set).sort((a, b) => a.localeCompare(b, "es"));
  }, [studentsWithStatus]);

  const filtered = useMemo(
    () =>
      studentsWithStatus
        .filter((s) => (activeOnly ? s.active : true))
        .filter((s) => (idsFilter ? idsFilter.has(s.id) : true))
        .filter((s) => matchesSearch(s, search))
        .filter((s) => {
          if (!teacherFilter) return true;
          return (s.assigned_teacher || "").toUpperCase() === teacherFilter;
        })
        .filter((s) => {
          if (docsFilter === "incomplete") return !s._docsStatus.complete;
          if (docsFilter === "complete") return s._docsStatus.complete;
          if (docsFilter === "invoice-pending") return s._docsStatus.invoicePending;
          if (docsFilter === "invoice-emitted") return s._docsStatus.invoiceEmittedCount > 0;
          // V1.27 · Filtros por etapa del ciclo comercial
          if (docsFilter.startsWith("stage-")) {
            const stage = docsFilter.replace("stage-", "");
            return (s.lifecycle_stage || "prospecto") === stage;
          }
          return true;
        })
        .filter((s) => {
          if (missingDoc === "all") return true;
          return s._docsStatus.missing.includes(missingDoc);
        }),
    [studentsWithStatus, activeOnly, idsFilter, search, teacherFilter, docsFilter, missingDoc]
  );

  // Show missing-doc chips in the row when any doc-related filter is active.
  const showMissingChips =
    docsFilter === "incomplete" ||
    docsFilter === "invoice-pending" ||
    missingDoc !== "all";

  // V1.27.1 · KPIs por etapa CRM. Se computan sobre TODOS los alumnos (no
  // sobre `filtered`) para que las tarjetas actúen como "dashboard general"
  // independientemente del filtro activo.
  const stageCounts = useMemo(() => {
    const counts = { prospecto: 0, interesado: 0, alumno_activo: 0, alumno_pausa: 0, baja: 0 };
    students.forEach((s) => {
      const st = s.lifecycle_stage || "prospecto";
      if (counts[st] !== undefined) counts[st] += 1;
    });
    return counts;
  }, [students]);

  const kpiCards = [
    { key: "prospecto",     label: "Prospectos",     icon: "🔵", count: stageCounts.prospecto,     ring: "ring-blue-400",    bg: "bg-blue-50 hover:bg-blue-100",       text: "text-blue-900",    border: "border-blue-200" },
    { key: "interesado",    label: "Interesados",    icon: "🟡", count: stageCounts.interesado,    ring: "ring-amber-400",   bg: "bg-amber-50 hover:bg-amber-100",     text: "text-amber-900",   border: "border-amber-200" },
    { key: "alumno_activo", label: "Alumnos activos",icon: "🟢", count: stageCounts.alumno_activo, ring: "ring-emerald-400", bg: "bg-emerald-50 hover:bg-emerald-100", text: "text-emerald-900", border: "border-emerald-200" },
    { key: "alumno_pausa",  label: "En pausa",       icon: "🟣", count: stageCounts.alumno_pausa,  ring: "ring-purple-400",  bg: "bg-purple-50 hover:bg-purple-100",   text: "text-purple-900",  border: "border-purple-200" },
    { key: "baja",          label: "Bajas",          icon: "⚫", count: stageCounts.baja,          ring: "ring-neutral-400", bg: "bg-neutral-50 hover:bg-neutral-100", text: "text-neutral-800", border: "border-neutral-200" },
  ];

  const activeStageKey = docsFilter.startsWith("stage-") ? docsFilter.replace("stage-", "") : null;
  const toggleStageFilter = useCallback((stageKey) => {
    setDocsFilter((prev) => (prev === `stage-${stageKey}` ? "all" : `stage-${stageKey}`));
  }, []);

  const toggleActive = useCallback(
    async (s) => {
      // V1.41 · Desactivar exige motivo → abre diálogo. Reactivar es directo.
      if (s.active) {
        setDeactivatingStudent(s);
        return;
      }
      try {
        await api.post(`/students/${s.id}/toggle`);
        toast.success("Alumno activado");
        load();
      } catch (err) {
        toast.error(formatApiError(err?.response?.data?.detail));
      }
    },
    [load]
  );

  // V1.26 · Duplicar alumno — copia sólo la dirección + abre el editor con
  // el nuevo alumno para que el usuario complete identidad/contacto.
  const duplicateStudent = useCallback(
    async (s) => {
      if (duplicating) return;
      setDuplicating(true);
      try {
        const { data } = await api.post(`/students/${s.id}/duplicate`);
        toast.success("Alumno duplicado correctamente. Revise los datos personales antes de guardar.");
        await load();
        // Abrimos el diálogo de edición sobre el nuevo alumno directamente
        // en modo "editar" para que el usuario complete los datos.
        setEditing(data);
      } catch (err) {
        toast.error(formatApiError(err?.response?.data?.detail));
      } finally {
        setDuplicating(false);
      }
    },
    [load, duplicating]
  );

  return (
    <div className="space-y-4">
      {/* V1.27.1 · Fila KPI clicable de estados CRM. Cada tarjeta filtra la
          lista por su lifecycle_stage. Un segundo clic desactiva el filtro. */}
      <div
        data-testid="crm-kpi-row"
        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2"
      >
        {kpiCards.map((card) => {
          const isActive = activeStageKey === card.key;
          return (
            <button
              key={card.key}
              type="button"
              onClick={() => toggleStageFilter(card.key)}
              data-testid={`crm-kpi-${card.key}`}
              aria-pressed={isActive}
              className={
                "group relative flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-all " +
                card.bg + " " + card.border + " " +
                (isActive
                  ? `ring-2 ${card.ring} shadow-sm scale-[1.01]`
                  : "hover:shadow-sm")
              }
            >
              <span className="text-xl leading-none">{card.icon}</span>
              <div className="min-w-0 flex-1">
                <div className={"text-[10px] uppercase font-semibold tracking-wide " + card.text + " opacity-70"}>
                  {card.label}
                </div>
                <div className={"text-lg font-bold leading-tight " + card.text}>
                  {card.count}
                </div>
              </div>
              {isActive && (
                <span
                  data-testid={`crm-kpi-${card.key}-active`}
                  className="absolute top-1 right-1 text-[9px] font-semibold text-neutral-500"
                  title="Filtro activo — haz clic de nuevo para desactivar"
                >
                  ●
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="flex flex-1 gap-2 items-center">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
            <input
              data-testid="students-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar alumno…"
              className="w-full rounded-xl border border-neutral-300 bg-white pl-10 pr-3 h-11 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
            />
          </div>
          <button
            data-testid="students-filter-active"
            onClick={() => setActiveOnly((v) => !v)}
            className={
              "btn-secondary !h-11 " +
              (!activeOnly ? "ring-2 ring-orange-500 border-orange-500" : "")
            }
            title={activeOnly ? "Mostrar todos los alumnos (incluidos bajas)" : "Volver a ver sólo activos"}
          >
            <Filter className="h-4 w-4" />
            <span className="hidden sm:inline">{activeOnly ? "Todos" : "Sólo activos"}</span>
          </button>
          {/* V1.38 · Filtro rápido por profesor asignado (desplegable) */}
          <select
            data-testid="students-filter-teacher"
            value={teacherFilter}
            onChange={(e) => setTeacherFilter(e.target.value)}
            className="rounded-xl border border-neutral-300 bg-white px-3 h-11 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
          >
            <option value="">Todos los profesores</option>
            {teacherOptions.map((o) => {
              const n = studentsWithStatus.filter(
                (s) => s.active && (s.assigned_teacher || "").toUpperCase() === o.code,
              ).length;
              return (
                <option key={o.code} value={o.code}>{o.label} ({o.code}) · {n}</option>
              );
            })}
          </select>
          {/* V1.39 · Chip para desactivar el filtro venido del Dashboard
              (rango de bono / listado de alumnos afectados). */}
          {idsFilter && (
            <button
              type="button"
              onClick={() => { setIdsFilter(null); setFilterLabel(""); setActiveOnly(true); }}
              data-testid="students-clear-ids-filter"
              className="inline-flex items-center gap-1 rounded-full bg-orange-50 border border-orange-300 text-orange-800 hover:bg-orange-100 text-xs font-semibold px-3 h-9"
              title="Quitar filtro venido del Dashboard"
            >
              {filterLabel ? `Filtro: ${filterLabel}` : "Filtro activo"} · quitar
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {/* V1.27 · Filtros rápidos consolidados en un único dropdown para
              reducir la saturación visual. Incluye docs, factura y ciclo. */}
          <Select value={docsFilter} onValueChange={setDocsFilter}>
            <SelectTrigger
              data-testid="students-docs-filter-select"
              className={
                "h-9 w-56 rounded-full text-xs " +
                (docsFilter !== "all"
                  ? "border-orange-500 ring-2 ring-orange-200"
                  : "")
              }
            >
              <div className="flex items-center gap-1.5 truncate">
                <Filter className="h-3.5 w-3.5 flex-shrink-0" />
                <SelectValue placeholder="📂 Filtros rápidos" />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">📂 Todos los documentos</SelectItem>
              <SelectItem value="incomplete">⚠️ Documentación incompleta</SelectItem>
              <SelectItem value="complete">✅ Documentación completa</SelectItem>
              <SelectItem value="invoice-pending">🧾 Factura pendiente</SelectItem>
              <SelectItem value="invoice-emitted">💶 Factura emitida</SelectItem>
              <SelectItem value="stage-prospecto">🔵 Solo prospectos</SelectItem>
              <SelectItem value="stage-interesado">🟡 Solo interesados</SelectItem>
              <SelectItem value="stage-alumno_activo">🟢 Solo alumnos activos</SelectItem>
              <SelectItem value="stage-alumno_pausa">🟣 Solo en pausa</SelectItem>
              <SelectItem value="stage-baja">⚫ Solo bajas</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setSheetOpen(true)}
              data-testid="students-printable-sheet-btn"
              title="Hoja imprimible: filtra, agrupa y exporta a Excel o PDF"
              className="h-9 rounded-full border border-orange-300 bg-orange-50 text-orange-700 px-3 text-xs font-bold inline-flex items-center gap-1.5 hover:bg-orange-100"
            >
              <FileSpreadsheet className="h-4 w-4" /> Hoja imprimible
            </button>
          </div>
        </div>
        {/* V1.27 · Reemplaza "Añadir alumno" por menú desplegable
            Prospecto / Alumno directo. Un prospecto es un contacto con
            solo nombre+teléfono; un alumno directo entra con ficha completa. */}
        <div className="relative group">
          <button
            data-testid="add-student-button"
            className="btn-primary"
          >
            <Plus className="h-4 w-4" />
            Nuevo registro
          </button>
          <div className="absolute right-0 top-full mt-1 min-w-[180px] rounded-lg border border-neutral-200 bg-white shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-20">
            <button
              type="button"
              data-testid="add-prospecto"
              onClick={() => setEditing({ _newStage: "prospecto" })}
              className="w-full text-left px-3 py-2 text-sm hover:bg-neutral-50 flex items-center gap-2"
            >
              <span className="h-2 w-2 rounded-full bg-blue-500" />
              Crear prospecto
            </button>
            <button
              type="button"
              data-testid="add-alumno-directo"
              onClick={() => setEditing({ _newStage: "alumno_activo" })}
              className="w-full text-left px-3 py-2 text-sm hover:bg-neutral-50 flex items-center gap-2 border-t border-neutral-100"
            >
              <span className="h-2 w-2 rounded-full bg-emerald-500" />
              Crear alumno directo
            </button>
            <button
              type="button"
              data-testid="create-documents-btn"
              onClick={() => onNavigate && onNavigate("crear_documentos")}
              className="w-full text-left px-3 py-2 text-sm hover:bg-neutral-50 flex items-center gap-2 border-t border-neutral-100"
            >
              <span className="h-2 w-2 rounded-full bg-orange-500" />
              Crear documentos
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={toggleStatsColumn}
          data-testid="toggle-stats-column"
          className={
            "hidden md:inline-flex items-center gap-1.5 h-10 px-3 rounded-lg border text-xs font-semibold transition-colors " +
            (showStatsColumn
              ? "border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
              : "border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50")
          }
          title="Mostrar/ocultar columna de estadísticas de clases"
        >
          {showStatsColumn ? "📊 Columna clases ON" : "📊 Columna clases OFF"}
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div className="card-surface p-8 text-center text-neutral-500">Cargando…</div>
      ) : filtered.length === 0 ? (
        <div className="card-surface p-10 text-center">
          <GraduationCap className="h-10 w-10 mx-auto text-neutral-300 mb-3" />
          <div className="font-semibold mb-1">No hay alumnos todavía</div>
          <p className="text-sm text-neutral-500">Crea el primer alumno para empezar.</p>
        </div>
      ) : (
        <>
          {/* Mobile: cards */}
          <div className="grid gap-3 md:hidden">
            {filtered.map((s) => (
              <div
                key={s.id}
                data-testid={`student-card-${s.id}`}
                className="card-surface p-3 min-h-[56px]"
              >
                <div className="grid grid-cols-[140px_1fr] gap-x-3 items-center mb-3">
                  {!s.active ? (
                    <button type="button" onClick={() => setEditingMotivoStudent(s)}
                      data-testid={`edit-motivo-${s.id}`}
                      title="Corregir motivo de cierre (Matriz)" className="text-left">
                      <StageBadge stage={s.lifecycle_stage} />
                    </button>
                  ) : (
                    <StageBadge stage={s.lifecycle_stage} />
                  )}
                  <div className="min-w-0">
                    <div className="font-semibold truncate" title={s.name}>
                      {studentShortLabel(s) || s.name}
                    </div>
                    <div className="text-xs text-neutral-500 flex items-center gap-1 mt-0.5 truncate">
                      <Phone className="h-3 w-3 shrink-0" />
                      {s.phone}
                    </div>
                    {s.email && (
                      <div className="text-xs text-neutral-500 flex items-center gap-1 mt-0.5 truncate">
                        <Mail className="h-3 w-3 shrink-0" />
                        <span className="truncate">{s.email}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between mb-3">
                  <span className={s.active ? "badge-active" : "badge-inactive"}>
                    {s.active ? "Activo" : "Inactivo"}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2 mb-3 text-[11px]">
                  {/* V1.37.13 · Chip "Docs OK/Faltan N" retirado (poca utilidad). */}
                  {s._docsStatus.invoiceEmittedCount > 0 ? (
                    <button
                      type="button"
                      onClick={() => setInvoicesPopoverStudent(s)}
                      data-testid={`invoice-chip-mobile-${s.id}`}
                      className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 font-semibold hover:bg-emerald-200 transition-colors"
                    >
                      <Receipt className="h-3 w-3" />
                      {s._docsStatus.invoiceEmittedCount} Factura{s._docsStatus.invoiceEmittedCount === 1 ? "" : "s"}
                    </button>
                  ) : s._docsStatus.invoicePending && (
                    <button
                      type="button"
                      onClick={() => setInvoicesPopoverStudent(s)}
                      data-testid={`invoice-chip-mobile-${s.id}`}
                      className="inline-flex items-center gap-1 rounded-full bg-orange-100 text-orange-800 px-2 py-0.5 font-semibold hover:bg-orange-200 transition-colors"
                    >
                      <Receipt className="h-3 w-3" />
                      Factura pendiente
                    </button>
                  )}
                </div>
                {showMissingChips && s._docsStatus.missing.length > 0 && (
                  <div
                    data-testid={`missing-docs-list-${s.id}`}
                    className="flex flex-wrap gap-1 mb-3"
                  >
                    {s._docsStatus.missing.map((name) => (
                      <span
                        key={name}
                        className={
                          "inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-medium " +
                          (name === missingDoc
                            ? "border-orange-500 bg-orange-50 text-orange-800"
                            : "border-neutral-200 bg-neutral-50 text-neutral-700")
                        }
                      >
                        {name}
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  <button
                    data-testid={`student-summary-${s.id}`}
                    onClick={() => setSummaryStudent(s)}
                    className="btn-ghost border border-neutral-200"
                  >
                    Resumen
                  </button>
                  <button
                    data-testid={`edit-student-${s.id}`}
                    onClick={() => setEditing(s)}
                    className="btn-ghost border border-neutral-200"
                  >
                    <Pencil className="h-4 w-4" /> Editar
                  </button>
                  <button
                    data-testid={`duplicate-student-${s.id}`}
                    onClick={() => duplicateStudent(s)}
                    disabled={duplicating}
                    className="btn-ghost border border-neutral-200 disabled:opacity-50"
                    title="Duplicar alumno (copia sólo la dirección)"
                  >
                    <Copy className="h-4 w-4" /> Duplicar
                  </button>
                  <button
                    data-testid={`toggle-student-${s.id}`}
                    onClick={() => toggleActive(s)}
                    className="btn-ghost border border-neutral-200"
                  >
                    <Power className="h-4 w-4" />
                    {s.active ? "Desactivar" : "Activar"}
                  </button>
                  {isSuperAdmin && (
                    <button
                      data-testid={`delete-student-${s.id}`}
                      onClick={() => setDeletingStudent(s)}
                      className="btn-ghost border border-red-200 text-red-700 hover:bg-red-50"
                      title="Eliminar alumno definitivamente"
                    >
                      <Trash2 className="h-4 w-4" /> Eliminar
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: table */}
          <div className="card-surface overflow-hidden hidden md:block">
            <table className="w-full text-sm table-fixed">
              <colgroup>
                {showStatsColumn && <col style={{ width: "180px" }} />}
                <col style={{ width: "140px" }} />
                <col />
                <col style={{ width: "150px" }} />
                <col style={{ width: "200px" }} />
                <col style={{ width: "100px" }} />
                <col style={{ width: "150px" }} />
                <col style={{ width: "280px" }} />
              </colgroup>
              <thead className="bg-neutral-50 text-neutral-600 text-left">
                <tr>
                  {showStatsColumn && (
                    <th className="px-4 py-3 font-semibold text-[10px] uppercase tracking-wider" data-testid="col-stats-header">
                      Clases
                    </th>
                  )}
                  <th className="px-4 py-3 font-semibold">Estado</th>
                  <th className="px-4 py-3 font-semibold">Alumno</th>
                  <th className="px-4 py-3 font-semibold">Teléfono</th>
                  <th className="px-4 py-3 font-semibold">Email</th>
                  <th className="px-4 py-3 font-semibold">Activo</th>
                  <th className="px-4 py-3 font-semibold">Facturas</th>
                  <th className="px-4 py-3 font-semibold text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => {
                  const stats = classStats[s.id];
                  return (
                  <tr
                    key={s.id}
                    data-testid={`student-row-${s.id}`}
                    className="border-t border-neutral-100 hover:bg-neutral-50/60 h-[56px]"
                  >
                    {showStatsColumn && (
                      <td className="px-4 py-2 align-middle" data-testid={`col-stats-${s.id}`}>
                        {stats ? (
                          <div className="grid grid-cols-3 gap-x-1.5 text-[10px] font-mono leading-tight">
                            <div title="C · Clases contratadas (compradas)"><span className="text-neutral-500">C:</span> <b>{stats.contracted}</b></div>
                            <div title="R · Clases realizadas (asistió)"><span className="text-neutral-500">R:</span> <b>{stats.done}</b></div>
                            <div title="P · Clases programadas, aún por dar (futuras)"><span className="text-neutral-500">P:</span> <b>{stats.scheduled}</b></div>
                            <div title="NA · Faltas (no asistió)"><span className="text-neutral-500">NA:</span> <b>{stats.missed}</b></div>
                            <div title="A · Ajustes manuales (+/−)"><span className="text-neutral-500">A:</span> <b className={stats.adjustments < 0 ? "text-red-700" : stats.adjustments > 0 ? "text-emerald-700" : ""}>{stats.adjustments > 0 ? "+" : ""}{stats.adjustments}</b></div>
                            <div title="Rest · Clases restantes = C − (R + P + NA) + A" className={"font-bold " + (stats.remaining < 0 ? "text-red-700" : stats.remaining < 3 ? "text-amber-700" : "text-emerald-700")}>
                              <span className="text-neutral-500">Rest:</span> {stats.remaining}
                            </div>
                          </div>
                        ) : (
                          <span className="text-[10px] text-neutral-300">···</span>
                        )}
                      </td>
                    )}
                    <td className="px-4 py-3 align-middle">
                      {!s.active ? (
                        <button type="button" onClick={() => setEditingMotivoStudent(s)}
                          data-testid={`edit-motivo-row-${s.id}`}
                          title="Corregir motivo de cierre (Matriz)" className="text-left">
                          <StageBadge stage={s.lifecycle_stage} />
                        </button>
                      ) : (
                        <StageBadge stage={s.lifecycle_stage} />
                      )}
                    </td>
                    <td className="px-4 py-3 font-semibold align-middle">
                      <span className="block truncate" title={s.name}>
                        {studentShortLabel(s) || s.name}
                      </span>
                    </td>
                    <td className="px-4 py-3 align-middle truncate">{s.phone}</td>
                    <td className="px-4 py-3 text-neutral-600 align-middle truncate" title={s.email || ""}>
                      {s.email || "—"}
                    </td>
                    <td className="px-4 py-3 align-middle">
                      <span className={s.active ? "badge-active" : "badge-inactive"}>
                        {s.active ? "Activo" : "Inactivo"}
                      </span>
                    </td>
                    <td className="px-4 py-3 align-middle">
                      <div className="flex flex-col gap-1.5 min-w-0">
                        <div className="flex flex-wrap gap-1 min-w-0">
                          {/* V1.37.13 · Chip "Faltan N/Docs OK" retirado — poca utilidad práctica. Sólo se conservan los chips de facturas. */}
                          {s._docsStatus.invoiceEmittedCount > 0 ? (
                            <button
                              type="button"
                              onClick={() => setInvoicesPopoverStudent(s)}
                              data-testid={`invoice-chip-${s.id}`}
                              className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 text-[11px] font-semibold hover:bg-emerald-200 transition-colors whitespace-nowrap max-w-full"
                              title={`Ver ${s._docsStatus.invoiceEmittedCount} factura(s) emitida(s)`}
                            >
                              <Receipt className="h-3 w-3 shrink-0" />
                              <span className="truncate">{s._docsStatus.invoiceEmittedCount} Factura{s._docsStatus.invoiceEmittedCount === 1 ? "" : "s"}</span>
                            </button>
                          ) : s._docsStatus.invoicePending && (
                            <button
                              type="button"
                              onClick={() => setInvoicesPopoverStudent(s)}
                              data-testid={`invoice-chip-${s.id}`}
                              className="inline-flex items-center gap-1 rounded-full bg-orange-100 text-orange-800 px-2 py-0.5 text-[11px] font-semibold hover:bg-orange-200 transition-colors whitespace-nowrap max-w-full"
                              title="Hay facturas en borrador — pulsa para ver"
                            >
                              <Receipt className="h-3 w-3 shrink-0" />
                              <span className="truncate">Factura pendiente</span>
                            </button>
                          )}
                        </div>
                        {showMissingChips && s._docsStatus.missing.length > 0 && (
                          <div
                            data-testid={`missing-docs-list-${s.id}`}
                            className="flex flex-wrap gap-1"
                          >
                            {s._docsStatus.missing.map((name) => (
                              <span
                                key={name}
                                className={
                                  "inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-medium " +
                                  (name === missingDoc
                                    ? "border-orange-500 bg-orange-50 text-orange-800"
                                    : "border-neutral-200 bg-neutral-50 text-neutral-700")
                                }
                              >
                                {name}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-middle">
                      <div className="flex justify-end items-center gap-1 whitespace-nowrap">
                        <button
                          data-testid={`student-summary-${s.id}`}
                          onClick={() => setSummaryStudent(s)}
                          className="btn-ghost"
                        >
                          Resumen
                        </button>
                        <button
                          data-testid={`edit-student-${s.id}`}
                          onClick={() => setEditing(s)}
                          className="btn-ghost"
                          title="Editar"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          data-testid={`duplicate-student-${s.id}`}
                          onClick={() => duplicateStudent(s)}
                          disabled={duplicating}
                          className="btn-ghost disabled:opacity-50"
                          title="Duplicar (copia sólo la dirección)"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                        <button
                          data-testid={`toggle-student-${s.id}`}
                          onClick={() => toggleActive(s)}
                          className="btn-ghost"
                          title={s.active ? "Desactivar" : "Activar"}
                        >
                          <Power className="h-4 w-4" />
                        </button>
                        {isSuperAdmin && (
                          <button
                            data-testid={`delete-student-${s.id}`}
                            onClick={() => setDeletingStudent(s)}
                            className="btn-ghost text-red-700 hover:bg-red-50"
                            title="Eliminar definitivamente"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );})}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end mt-3">
            <button
              type="button"
              onClick={() => setShowExport(true)}
              data-testid="export-students-btn"
              className="btn-secondary !h-9 text-xs"
            >
              <Download className="h-4 w-4" /> Exportar listado
            </button>
          </div>
        </>
      )}

      <StudentDialog
        open={editing !== null}
        student={editing && editing.id ? editing : null}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          load();
        }}
      />

      <StudentSummaryDialog
        student={summaryStudent}
        onClose={() => setSummaryStudent(null)}
      />
      <InvoicesDetailDialog
        open={!!invoicesPopoverStudent}
        student={invoicesPopoverStudent}
        invoices={invoicesPopoverStudent ? studentInvoices(invoicesPopoverStudent) : []}
        onClose={() => setInvoicesPopoverStudent(null)}
      />
      <MissingDocsDialog
        open={!!missingDocsStudent}
        student={missingDocsStudent}
        missing={
          missingDocsStudent
            ? (missingDocsStudent._docsStatus?.missing || []).filter((n) => n !== "Factura emitida")
            : []
        }
        onClose={() => setMissingDocsStudent(null)}
      />
      <DeactivateStudentDialog
        open={!!editingMotivoStudent}
        student={editingMotivoStudent}
        mode="edit"
        onClose={() => setEditingMotivoStudent(null)}
        onDone={() => {
          setEditingMotivoStudent(null);
          load();
        }}
      />
      <DeactivateStudentDialog
        open={!!deactivatingStudent}
        student={deactivatingStudent}
        onClose={() => setDeactivatingStudent(null)}
        onDone={() => {
          setDeactivatingStudent(null);
          load();
        }}
      />
      <DeleteStudentDialog
        open={!!deletingStudent}
        student={deletingStudent}
        onClose={() => setDeletingStudent(null)}
        onDeleted={() => {
          setDeletingStudent(null);
          load();
        }}
      />
      <ExportStudentsDialog open={showExport} onClose={() => setShowExport(false)} />
      <PrintableSheetDialog
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        students={filtered}
      />
    </div>
  );
}
